// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Button,
    Icon,
    Switch,
    Translate,
} from '@plesk/ui-library';
import { IFailoverDomainDetailResponse } from 'common/api/resources/FailoverDomain';
import { Header } from 'admin/common/components/PageHeader/Styles';
import {
    ActionsContainer,
    ActiveLabel,
    BreadcrumbLink,
    BreadcrumbSep,
    DocumentationLink,
    DomainName,
    LocationName,
    SubTitleRow,
    TitleContainer,
    TitleRow,
} from 'admin/failoverDomain/page/components/PageHeader/Styles';
import { GreenSwitch } from 'admin/failoverDomain/containers/Styles';
import { pathTo } from 'common/helpers/core';

export interface IPageHeaderProps {
    failoverDomain: IFailoverDomainDetailResponse;
    onToggleActive: (isActive: boolean) => void;
    onSettingsClick: () => void;
    isToggling?: boolean;
    trailingBreadcrumb?: React.ReactNode;
}

const PageHeader: React.FC<IPageHeaderProps> = ({
    failoverDomain,
    onToggleActive,
    onSettingsClick,
    isToggling,
    trailingBreadcrumb,
}) => (
    <Header>
        <TitleContainer>
            <TitleRow>
                <BreadcrumbLink to={pathTo('failover_domains')}>
                    <Translate content="failoverDomain.title" />
                </BreadcrumbLink>
                <BreadcrumbSep>›</BreadcrumbSep>
                {trailingBreadcrumb ? (
                    <>
                        <BreadcrumbLink to={pathTo(`failover_domains/${failoverDomain.id}`)}>
                            {failoverDomain.name}
                        </BreadcrumbLink>
                        <BreadcrumbSep>›</BreadcrumbSep>
                        <DomainName>{trailingBreadcrumb}</DomainName>
                    </>
                ) : (
                    <DomainName>{failoverDomain.name}</DomainName>
                )}
            </TitleRow>
            <SubTitleRow>
                <LocationName>
                    {failoverDomain.location.icon && (
                        <img
                            src={failoverDomain.location.icon.url}
                            alt={failoverDomain.location.name}
                            style={{ width: 20, height: 14 }}
                        />
                    )}
                    {failoverDomain.location.name}
                </LocationName>
            </SubTitleRow>
        </TitleContainer>
        <ActionsContainer>
            <DocumentationLink href="https://docs.solusvm.com" target="_blank" rel="noopener noreferrer">
                <Icon name="info-circle" size={16} />
                <Translate content="failoverDomain.page.documentation" />
            </DocumentationLink>
            <GreenSwitch>
                <Switch
                    checked={failoverDomain.is_active}
                    onChange={(checked: boolean) => onToggleActive(checked)}
                    loading={isToggling}
                />
            </GreenSwitch>
            <ActiveLabel>
                <Translate content="failoverDomain.list.active" />
            </ActiveLabel>
            <Button
                ghost={true}
                icon={<Icon name="gear" size="24"/>}
                onClick={onSettingsClick}
            />
        </ActionsContainer>
    </Header>
);

export default PageHeader;
